import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PlayIcon, DocumentDuplicateIcon, CodeBracketIcon, KeyIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { APIEndpoint, APIKey } from '@/types'
import { fetchAPIEndpoints, testAPIEndpoint, fetchAPIKeys } from '@/services/api'

const mockEndpoints: APIEndpoint[] = [
  {
    id: 'GET_/v1/users',
    path: '/v1/users',
    method: 'GET',
    description: 'List all users with pagination',
    parameters: [
      { name: 'limit', type: 'integer', required: false, description: 'Number of users to return', in: 'query' },
      { name: 'offset', type: 'integer', required: false, description: 'Number of users to skip', in: 'query' },
    ],
    responses: [
      { status: 200, description: 'List of users' },
      { status: 401, description: 'Unauthorized' }
    ],
    authentication: true
  },
  {
    id: 'POST_/v1/users',
    path: '/v1/users',
    method: 'POST',
    description: 'Create a new user',
    parameters: [
      { name: 'name', type: 'string', required: true, description: 'User name', in: 'body' },
      { name: 'email', type: 'string', required: true, description: 'User email', in: 'body' },
    ],
    responses: [
      { status: 201, description: 'User created successfully' },
      { status: 400, description: 'Invalid input' }
    ],
    authentication: true
  },
  {
    id: 'GET_/v1/users/:id',
    path: '/v1/users/{id}',
    method: 'GET',
    description: 'Get user by ID',
    parameters: [
      { name: 'id', type: 'string', required: true, description: 'User ID', in: 'path' },
    ],
    responses: [
      { status: 200, description: 'User details' },
      { status: 404, description: 'User not found' }
    ],
    authentication: true
  }
]

export default function APIExplorer() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpoint | null>(null)
  const [parameters, setParameters] = useState<Record<string, any>>({})
  const [response, setResponse] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [selectedApiKey, setSelectedApiKey] = useState<string>('')

  const { data: endpoints = mockEndpoints, isLoading } = useQuery({
    queryKey: ['endpoints'],
    queryFn: fetchAPIEndpoints,
  })

  const { data: apiKeys = [] } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: fetchAPIKeys,
  })

  // Set API key in localStorage when selected
  useEffect(() => {
    if (selectedApiKey) {
      localStorage.setItem('api_key', selectedApiKey)
    }
  }, [selectedApiKey])

  // Load saved API key on mount
  useEffect(() => {
    const savedApiKey = localStorage.getItem('api_key')
    if (savedApiKey) {
      setSelectedApiKey(savedApiKey)
    } else if (apiKeys.length > 0) {
      // Auto-select first API key if none selected
      setSelectedApiKey(apiKeys[0].key)
    }
  }, [apiKeys])

  const handleParameterChange = (paramName: string, value: any) => {
    setParameters(prev => ({
      ...prev,
      [paramName]: value
    }))
  }

  const handleTestEndpoint = async () => {
    if (!selectedEndpoint) return

    // Check if API key is required but not selected
    if (selectedEndpoint.authentication && !selectedApiKey) {
      toast.error('Please select an API key for authenticated endpoints')
      return
    }

    setLoading(true)
    try {
      const result = await testAPIEndpoint(selectedEndpoint, parameters)
      setResponse(result)
      toast.success('Request completed successfully')
    } catch (error: any) {
      setResponse({
        error: true,
        message: error.response?.data?.message || error.message,
        status: error.response?.status
      })
      toast.error('Request failed')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const generateCurlCommand = () => {
    if (!selectedEndpoint) return ''

    let curl = `curl -X ${selectedEndpoint.method} \\\n`
    curl += `  "${import.meta.env.VITE_API_BASE_URL || 'https://api.carboat.org'}${selectedEndpoint.path}" \\\n`
    curl += `  -H "Content-Type: application/json"`
    
    if (selectedEndpoint.authentication) {
      curl += ` \\\n  -H "X-API-Key: ${selectedApiKey || 'YOUR_API_KEY'}"`
    }

    if (selectedEndpoint.method === 'POST' || selectedEndpoint.method === 'PUT') {
      const bodyParams = selectedEndpoint.parameters?.filter(p => p.in === 'body') || []
      if (bodyParams.length > 0) {
        const body = bodyParams.reduce((acc, param) => {
          acc[param.name] = parameters[param.name] || `<${param.type}>`
          return acc
        }, {} as Record<string, any>)
        curl += ` \\\n  -d '${JSON.stringify(body, null, 2)}'`
      }
    }

    return curl
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="h-96 bg-gray-200 rounded"></div>
          <div className="lg:col-span-2 h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">API Explorer</h1>
        <p className="mt-2 text-sm text-gray-700">
          Test your API endpoints interactively
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Endpoints List */}
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Endpoints</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {endpoints.map((endpoint) => (
              <button
                key={endpoint.id}
                onClick={() => {
                  setSelectedEndpoint(endpoint)
                  setParameters({})
                  setResponse(null)
                }}
                className={`w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors ${
                  selectedEndpoint?.id === endpoint.id ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                    endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                    endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {endpoint.method}
                  </span>
                  <code className="text-sm text-gray-900">{endpoint.path}</code>
                </div>
                <p className="mt-1 text-sm text-gray-500">{endpoint.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Request Builder & Response */}
        <div className="lg:col-span-2 space-y-6">
          {selectedEndpoint ? (
            <>
              {/* Request Builder */}
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Request</h3>
                  <button
                    onClick={() => copyToClipboard(generateCurlCommand())}
                    className="btn-secondary text-sm"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                    Copy cURL
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 text-sm font-medium rounded ${
                      selectedEndpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                      selectedEndpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      selectedEndpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedEndpoint.method}
                    </span>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {selectedEndpoint.path}
                    </code>
                  </div>

                  <p className="text-sm text-gray-600">{selectedEndpoint.description}</p>

                  {/* API Key Selector */}
                  {selectedEndpoint.authentication && (
                    <div>
                      <label className="label">
                        <KeyIcon className="h-4 w-4 inline mr-1" />
                        API Key
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select
                        value={selectedApiKey}
                        onChange={(e) => setSelectedApiKey(e.target.value)}
                        className="input"
                      >
                        <option value="">Select an API key...</option>
                        {apiKeys.map((key: APIKey) => (
                          <option key={key.id} value={key.key}>
                            {key.name} ({key.key.substring(0, 12)}...)
                          </option>
                        ))}
                      </select>
                      {!selectedApiKey && (
                        <p className="text-sm text-amber-600 mt-1">
                          ⚠️ Please select an API key to test authenticated endpoints
                        </p>
                      )}
                    </div>
                  )}

                  {/* Parameters */}
                  {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Parameters</h4>
                      <div className="space-y-3">
                        {selectedEndpoint.parameters.map((param) => (
                          <div key={param.name}>
                            <label className="label">
                              {param.name}
                              {param.required && <span className="text-red-500 ml-1">*</span>}
                              <span className="text-xs text-gray-500 ml-2">({param.type})</span>
                            </label>
                            <input
                              type={param.type === 'integer' ? 'number' : 'text'}
                              className="input"
                              placeholder={param.description}
                              value={parameters[param.name] || ''}
                              onChange={(e) => handleParameterChange(param.name, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleTestEndpoint}
                    disabled={loading}
                    className="btn-primary"
                  >
                    <PlayIcon className="h-4 w-4 mr-2" />
                    {loading ? 'Testing...' : 'Test Endpoint'}
                  </button>
                </div>
              </div>

              {/* Response */}
              {response && (
                <div className="card p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Response</h3>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
                    <pre className="text-green-400 text-sm">
                      {JSON.stringify(response, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="card p-12 text-center">
              <div className="text-gray-400">
                <CodeBracketIcon className="h-12 w-12 mx-auto mb-4" />
                <p>Select an endpoint to start testing</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
